import React, { useState, useEffect } from "react";
import { Loader } from "../Loader";
import { Button } from "../Button";
import {
  getNewsletterInsightsForAdmin,
  SingleSendStatsViewModel,
} from "../../api/communications";

const toISOStart = (date: string) => `${date}T00:00:00+00:00`;
const toISOEnd = (date: string) => `${date}T23:59:59+00:00`;

const n = (val: number | null | undefined) => val ?? 0;
const pct = (val: number | null | undefined) => `${n(val).toFixed(2)}%`;

const STATS: {
  label: string;
  value: (s: SingleSendStatsViewModel) => string;
  color: string;
}[] = [
  { label: "Sent",         value: (s) => n(s.requests).toLocaleString(),   color: "text-gray-800"   },
  { label: "Opened",       value: (s) => n(s.opens).toLocaleString(),       color: "text-green-600"  },
  { label: "Unique Opens", value: (s) => n(s.uniqueOpens).toLocaleString(), color: "text-green-600"  },
  { label: "Open Rate",    value: (s) => pct(s.openRate),                   color: "text-green-600"  },
  { label: "Clicks",       value: (s) => n(s.clicks).toLocaleString(),      color: "text-indigo-500" },
  { label: "CTR",          value: (s) => pct(s.ctr),                        color: "text-indigo-500" },
  { label: "CTOR",         value: (s) => pct(s.ctor),                       color: "text-indigo-500" },
  { label: "Unsubscribes", value: (s) => n(s.unsubscribes).toLocaleString(),color: "text-red-500"    },
  { label: "Bounces",      value: (s) => n(s.bounces).toLocaleString(),     color: "text-orange-500" },
];

const SingleSendRow: React.FC<{ send: SingleSendStatsViewModel }> = ({ send }) => (
  <div className="bg-white rounded-lg shadow p-4 mb-4">
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <span className="font-medium text-gray-800">{send.name}</span>
      {send.sendAt && (
        <span className="text-sm text-gray-500">
          {new Date(send.sendAt).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
      {send.status && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
          {send.status}
        </span>
      )}
    </div>

    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
      {STATS.map((stat) => (
        <div key={stat.label}>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
            {stat.label}
          </div>
          <div className={`text-lg font-bold ${stat.color}`}>
            {stat.value(send)}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const NewsletterTab: React.FC = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState<SingleSendStatsViewModel[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInsights = async () => {
    const dateParams = {
      from: from ? toISOStart(from) : undefined,
      to: to ? toISOEnd(to) : undefined,
    };

    setLoading(true);

    try {
      const result = await getNewsletterInsightsForAdmin(dateParams);
      setData(result);
      setError(null);
    } catch (err) {
      setError("Failed to fetch newsletter insights");
      console.error("Error fetching newsletter insights:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {/* Date range */}
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
        </div>
        <Button onClick={fetchInsights}>Apply</Button>
      </div>

      <section>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-medium">Newsletter (Sendgrid)</h3>
          <button
            onClick={() => setSortAsc((v) => !v)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            Date {sortAsc ? "↑" : "↓"}
          </button>
        </div>
        {error && <div className="error mb-4">{error}</div>}
        {data && (
          data.length === 0 ? (
            <div className="text-center text-gray-500 my-8">No campaigns found</div>
          ) : (
            [...data]
              .sort((a, b) => {
                const ta = a.sendAt ? new Date(a.sendAt).getTime() : 0;
                const tb = b.sendAt ? new Date(b.sendAt).getTime() : 0;
                return sortAsc ? ta - tb : tb - ta;
              })
              .map((send) => (
                <SingleSendRow key={send.id} send={send} />
              ))
          )
        )}
      </section>
    </div>
  );
};

export default NewsletterTab;
